import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Play, Star, CheckCircle, Clock, Users, Award, TrendingUp, Sparkles, BookOpen, Zap, Target, ChevronDown, ChevronUp, Shield, ArrowRight, Rocket, DollarSign, Package } from 'lucide-react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { siteVideoService } from '@/services/siteVideoService';
import { setEnrollmentProduct } from '@/redux/actions/enrollment';
import { trackCTA, trackVideo, trackPageView, trackAccordion } from '@/services/analytics';
import { useScrollTracking } from '@/hooks/useAnalytics';
import { useProductPricing, formatPrice } from '@/hooks/useFeaturedPricing';
import { metaPixel } from '@/services/metaPixel';

export default function AIEssentialsBundle() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [videoUrl, setVideoUrl] = useState<string | null>(null);

  // Fetch dynamic pricing from API - SINGLE SOURCE OF TRUTH
  const {
    pricing,
    loading: pricingLoading,
    displayPrice,
    originalPrice,
    isSaleActive,
    savingsAmount,
    savingsPercentage
  } = useProductPricing('ai-essentials-bundle', 'bundle');

  // Also fetch individual course pricing for comparison section
  const {
    displayPrice: pemDisplayPrice,
    originalPrice: pemOriginalPrice,
    loading: pemLoading
  } = useProductPricing('prompt-engineering-mastery', 'course');

  const {
    displayPrice: ataDisplayPrice,
    originalPrice: ataOriginalPrice,
    loading: ataLoading
  } = useProductPricing('ai-tools-accelerator', 'course');

  // Calculate combined individual price (what you'd pay buying separately)
  const combinedIndividualPrice = pemDisplayPrice + ataDisplayPrice;

  // Calculate per-course price (bundle has 2 courses)
  const perCoursePrice = displayPrice > 0 ? Math.round(displayPrice / 2) : 0;

  useScrollTracking('AIEssentialsBundle');

  // State for FAQ expansion
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  // Handle FAQ click with tracking
  const handleFaqClick = (idx: number, question: string) => {
    const isExpanding = expandedFaq !== idx;
    setExpandedFaq(isExpanding ? idx : null);
    if (isExpanding) {
      trackAccordion.questionExpand('faq-aieb-' + idx, question, 'AIEssentialsBundle');
    }
  };

  useEffect(() => {
    // Meta Pixel
    metaPixel.init();
    metaPixel.pageView();
    trackPageView('AIEssentialsBundle');
  }, []);

  // Fetch AIEB hero video
  useEffect(() => {
    const fetchVideo = async () => {
      try {
        const videoData = await siteVideoService.getSiteVideo('aieb_hero');
        if (videoData.playback_url) {
          setVideoUrl(videoData.playback_url);
        }
      } catch (error) {
      }
    };

    fetchVideo();
  }, []);

  // Handle enrollment click
  const handleEnrollClick = () => {
    trackCTA.enrollCtaClick('ai-essentials-bundle', 'hero', 147);
    // Set enrollment product using bundle slug (stable identifier)
    dispatch(setEnrollmentProduct(
      'ai-essentials-bundle-prompt-engineering-mastery-ai-tools-accelerator',
      'bundle'
    ) as any);
    navigate('/enrollment');
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <Helmet>
        <title>AI Essentials Bundle - AI Mastery Hub</title>
        <meta name="description" content="Master Prompt Engineering + AI Tools in one complete package. Save 58% on the ultimate AI learning bundle." />
      </Helmet>

      <Navigation />

      {/* Hero Section with Video */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-blue-900/20 to-cyan-900/20"></div>
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(139, 92, 246, 0.2) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(59, 130, 246, 0.15) 0%, transparent 50%)',
        }}></div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <div className="inline-block px-6 py-2 bg-gradient-to-r from-purple-600/20 via-blue-600/20 to-cyan-600/20 border border-purple-500/30 rounded-full text-sm mb-6 animate-pulse">
              <span className="bg-gradient-to-r from-purple-400 via-blue-400 to-cyan-400 bg-clip-text text-transparent font-semibold">
                {isSaleActive && savingsAmount > 0
                  ? `💎 BEST VALUE • SAVE ${formatPrice(savingsAmount)} • ${savingsPercentage}% OFF`
                  : '💎 BEST VALUE BUNDLE'
                }
              </span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
              <span className="block">AI Essentials</span>
              <span className="block bg-gradient-to-r from-purple-400 via-blue-400 to-cyan-400 bg-clip-text text-transparent">
                Complete Bundle
              </span>
            </h1>
            <p className="text-2xl text-gray-300 mb-8 max-w-4xl mx-auto leading-relaxed">
              Master <span className="text-purple-400 font-semibold">Prompt Engineering</span> + <span className="text-blue-400 font-semibold">20+ AI Tools</span> in One Complete Package. Everything You Need to Become an <span className="text-cyan-400 font-semibold">AI Power User</span>
            </p>

            {/* Value Proposition */}
            <div className="flex items-center justify-center gap-8 mb-12">
              <div className="flex items-center gap-2">
                <Package className="w-6 h-6 text-purple-400" />
                <span className="text-sm text-gray-300 font-medium">2 Complete Courses</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-6 h-6 text-blue-400" />
                <span className="text-sm text-gray-300 font-medium">15 Hours Training</span>
              </div>
              <div className="flex items-center gap-2">
                <DollarSign className="w-6 h-6 text-green-400" />
                <span className="text-sm text-gray-300 font-medium">$3,000+ in Resources</span>
              </div>
            </div>

            {/* Social Proof */}
            <div className="flex items-center justify-center gap-8 mb-12">
              <div className="flex items-center gap-2">
                <div className="flex -space-x-1">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-400 via-blue-400 to-cyan-400 border-2 border-black"></div>
                  ))}
                </div>
                <span className="text-sm text-gray-400">Rapidly growing enrollment</span>
              </div>
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                ))}
                <span className="text-sm text-gray-400 ml-2">Comprehensive 15-hour curriculum</span>
              </div>
            </div>
          </div>

          {/* Video Section */}
          <div className="max-w-5xl mx-auto mb-12">
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-purple-600 via-blue-600 to-cyan-600 rounded-3xl blur-2xl opacity-40 group-hover:opacity-60 transition-all animate-pulse"></div>
              <div className="relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-3xl p-4 border border-white/20">
                <div className="relative aspect-video bg-gradient-to-br from-purple-900/50 via-blue-900/50 to-cyan-900/50 rounded-2xl overflow-hidden">
                  {videoUrl ? (
                    <video
                      src={videoUrl}
                      controls
                      onPlay={() => trackVideo.play('aieb-hero-video', videoUrl, 'AIEssentialsBundle')}
                      className="w-full h-full object-cover"
                      poster="https://images.unsplash.com/photo-1677442136019-21780ecad995?w=1200&h=675&fit=crop"
                    />
                  ) : (
                    <>
                      <img
                        src="https://images.unsplash.com/photo-1677442136019-21780ecad995?w=1200&h=675&fit=crop"
                        alt="Bundle Preview"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/40 hover:bg-black/30 transition-all cursor-pointer group">
                        <div className="w-24 h-24 bg-gradient-to-br from-purple-500 via-blue-500 to-cyan-500 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform shadow-2xl">
                          <Play className="w-10 h-10 text-white fill-white ml-1" />
                        </div>
                      </div>
                      <div className="absolute top-4 right-4 bg-black/80 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20">
                        <span className="text-sm font-semibold">5:30 Full Overview</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <button
              onClick={handleEnrollClick}
              className="bg-gradient-to-r from-purple-600 via-blue-600 to-cyan-600 hover:from-purple-500 hover:via-blue-500 hover:to-cyan-500 px-12 py-5 rounded-xl font-bold text-xl flex items-center space-x-3 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-purple-500/50 group"
            >
              <span>Get Bundle Now - {pricingLoading ? '...' : formatPrice(displayPrice)}</span>
              <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
            </button>
            {isSaleActive && savingsAmount > 0 && (
              <div className="text-center">
                <div className="text-3xl text-gray-300 line-through font-semibold decoration-2">{formatPrice(originalPrice)}</div>
                <div className="text-sm text-green-400 font-semibold">Save {formatPrice(savingsAmount)} ({savingsPercentage}% OFF)</div>
              </div>
            )}
          </div>

          {/* Trust Badges */}
          <div className="flex flex-wrap items-center justify-center gap-6 mt-12 text-sm text-gray-400">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-400" />
              <span>Instant Access to Both Courses</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-400" />
              <span>Expert Support</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-400" />
              <span>2 Certificates Included</span>
            </div>
          </div>
        </div>
      </section>

      {/* Bundle Comparison */}
      <section className="py-20 px-6 bg-gradient-to-br from-purple-900/10 via-blue-900/10 to-cyan-900/10">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-6">Why Choose the Bundle?</h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Get both courses for less than the price of one. The ultimate AI learning package.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Individual Course 1 */}
            <div className="bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-sm rounded-2xl p-8 border border-white/10">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-violet-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-8 h-8" />
                </div>
                <h3 className="text-2xl font-bold mb-2">Prompt Engineering</h3>
                <div className="text-4xl font-bold text-purple-400 mb-2">
                  {pemLoading ? '...' : formatPrice(pemDisplayPrice)}
                </div>
                <div className="text-sm text-gray-400">Individual Course</div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                  <span className="text-gray-300">7 hours training</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                  <span className="text-gray-300">12+ templates</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                  <span className="text-gray-300">1 certificate</span>
                </div>
              </div>
            </div>

            {/* Individual Course 2 */}
            <div className="bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-sm rounded-2xl p-8 border border-white/10">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Zap className="w-8 h-8" />
                </div>
                <h3 className="text-2xl font-bold mb-2">AI Tools</h3>
                <div className="text-4xl font-bold text-blue-400 mb-2">
                  {ataLoading ? '...' : formatPrice(ataDisplayPrice)}
                </div>
                <div className="text-sm text-gray-400">Individual Course</div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                  <span className="text-gray-300">8 hours training</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                  <span className="text-gray-300">20+ tools</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                  <span className="text-gray-300">1 certificate</span>
                </div>
              </div>
            </div>

            {/* Bundle */}
            <div className="relative">
              <div className="absolute -inset-1 bg-gradient-to-r from-purple-600 via-blue-600 to-cyan-600 rounded-2xl blur-xl opacity-50 animate-pulse"></div>
              <div className="relative bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-xl rounded-2xl p-8 border-2 border-white/30">
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-green-500 to-emerald-500 px-6 py-2 rounded-full text-sm font-bold">
                  BEST VALUE
                </div>
                <div className="text-center mb-6 pt-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-600 via-blue-600 to-cyan-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Package className="w-8 h-8" />
                  </div>
                  <h3 className="text-2xl font-bold mb-2">Complete Bundle</h3>
                  <div className="text-5xl font-bold bg-gradient-to-r from-purple-400 via-blue-400 to-cyan-400 bg-clip-text text-transparent mb-2">
                    {pricingLoading ? '...' : formatPrice(displayPrice)}
                  </div>
                  {isSaleActive && savingsAmount > 0 && (
                    <>
                      <div className="text-lg text-gray-400 line-through mb-1">{formatPrice(originalPrice)}</div>
                      <div className="text-sm text-green-400 font-bold">Save {formatPrice(savingsAmount)} ({savingsPercentage}%)</div>
                    </>
                  )}
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                    <span className="text-gray-300 font-semibold">15 hours training</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                    <span className="text-gray-300 font-semibold">$3,000+ resources</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                    <span className="text-gray-300 font-semibold">2 certificates</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                    <span className="text-gray-300 font-semibold">Complete AI mastery</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            {isSaleActive && savingsAmount > 0 && (
              <>
                <p className="text-2xl text-gray-300 mb-4">
                  <span className="text-red-400 line-through font-semibold">{formatPrice(originalPrice)}</span> if purchased separately
                </p>
                <p className="text-xl text-green-400 font-bold">
                  Pay just {formatPrice(displayPrice)} and save {formatPrice(savingsAmount)} with the bundle
                </p>
              </>
            )}
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-24 px-6 bg-black">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-6">Everything You Get</h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Two complete courses, over $3,000 in resources, and lifetime access
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 mb-16">
            {/* Course 1 */}
            <div className="bg-gradient-to-br from-purple-900/20 to-transparent rounded-3xl p-8 border border-purple-500/30">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-violet-600 rounded-2xl flex items-center justify-center">
                  <Sparkles className="w-8 h-8" />
                </div>
                <div>
                  <h3 className="text-3xl font-bold">Prompt Engineering Mastery</h3>
                  <p className="text-purple-400">Course #1 - 7 Hours</p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold text-white mb-1">7 Core Prompting Skills</div>
                    <p className="text-sm text-gray-400">Master the techniques professionals use daily</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold text-white mb-1">CLEAR Framework & Templates</div>
                    <p className="text-sm text-gray-400">12+ proven prompt templates for instant results</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold text-white mb-1">Personal Prompt Library</div>
                    <p className="text-sm text-gray-400">Build your collection worth $1000s</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold text-white mb-1">Real-World Applications</div>
                    <p className="text-sm text-gray-400">Content, research, communication workflows</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Course 2 */}
            <div className="bg-gradient-to-br from-blue-900/20 to-transparent rounded-3xl p-8 border border-blue-500/30">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-2xl flex items-center justify-center">
                  <Zap className="w-8 h-8" />
                </div>
                <div>
                  <h3 className="text-3xl font-bold">AI Tools Accelerator</h3>
                  <p className="text-blue-400">Course #2 - 8 Hours</p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold text-white mb-1">20+ Essential AI Tools</div>
                    <p className="text-sm text-gray-400">ChatGPT, Claude, Midjourney, HeyGen & more</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold text-white mb-1">Complete Tool Mastery</div>
                    <p className="text-sm text-gray-400">LLMs, image/video gen, voice AI, development</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold text-white mb-1">Integration Guides</div>
                    <p className="text-sm text-gray-400">$2000+ in templates and workflow guides</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold text-white mb-1">Complete Workflows</div>
                    <p className="text-sm text-gray-400">Content, marketing, development end-to-end</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bonus Items */}
          <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-3xl p-8 border border-white/20">
            <h3 className="text-3xl font-bold mb-8 text-center">Plus These Bonuses</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-blue-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Award className="w-6 h-6" />
                </div>
                <h4 className="font-bold mb-2">2 Certificates</h4>
                <p className="text-sm text-gray-400">Professional certifications for both courses</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Rocket className="w-6 h-6" />
                </div>
                <h4 className="font-bold mb-2">Lifetime Updates</h4>
                <p className="text-sm text-gray-400">Get new content as AI evolves forever</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-gradient-to-br from-cyan-600 to-teal-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Users className="w-6 h-6" />
                </div>
                <h4 className="font-bold mb-2">Community Access</h4>
                <p className="text-sm text-gray-400">Join our growing AI learning community</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 px-6 bg-gradient-to-br from-purple-900/10 via-blue-900/10 to-cyan-900/10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-6">Bundle Student Success Stories</h2>
            <p className="text-xl text-gray-400">See how the complete bundle transformed their AI journey</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: 'Alex Thompson',
                role: 'Digital Marketer',
                image: 'from-purple-400 to-fuchsia-400',
                text: 'This bundle is the best investment I\'ve made. I went from AI novice to building complete automated workflows in just 3 weeks.'
              },
              {
                name: 'Lisa Chen',
                role: 'Entrepreneur',
                image: 'from-blue-400 to-cyan-400',
                text: 'I saved over $15k in agency fees by mastering these tools myself. The bundle paid for itself 30x over in the first month.'
              },
              {
                name: 'Marcus Johnson',
                role: 'Content Creator',
                image: 'from-cyan-400 to-teal-400',
                text: 'The combination of prompting skills and tool mastery is unbeatable. I\'ve 5x my output while cutting my worktime in half.'
              }
            ].map((testimonial, idx) => (
              <div key={idx} className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-300 mb-6 leading-relaxed">"{testimonial.text}"</p>
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${testimonial.image}`}></div>
                  <div>
                    <div className="font-semibold">{testimonial.name}</div>
                    <div className="text-sm text-gray-400">{testimonial.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-24 px-6 bg-gradient-to-br from-purple-900/20 via-blue-900/20 to-cyan-900/20">
        <div className="max-w-4xl mx-auto">
          <div className="relative group">
            <div className="absolute -inset-2 bg-gradient-to-r from-purple-600 via-blue-600 to-cyan-600 rounded-3xl blur-3xl opacity-40 group-hover:opacity-60 transition-all animate-pulse"></div>
            <div className="relative bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-2xl rounded-3xl border-2 border-white/30 p-12">
              <div className="text-center mb-8">
                {isSaleActive && savingsAmount > 0 && (
                  <div className="inline-block px-6 py-2 bg-gradient-to-r from-purple-600/30 via-blue-600/30 to-cyan-600/30 border border-purple-500/50 rounded-full mb-4 animate-pulse">
                    <span className="text-sm font-bold bg-gradient-to-r from-purple-400 via-blue-400 to-cyan-400 bg-clip-text text-transparent">
                      ⏰ LIMITED TIME OFFER - SAVE {formatPrice(savingsAmount)}
                    </span>
                  </div>
                )}
                <h2 className="text-5xl font-bold mb-4">Get the Complete Bundle</h2>
                <p className="text-xl text-gray-300">Join students mastering AI</p>
              </div>

              <div className="flex items-center justify-center gap-4 mb-8">
                <div className="text-center">
                  <div className="text-7xl font-bold bg-gradient-to-r from-purple-400 via-blue-400 to-cyan-400 bg-clip-text text-transparent mb-2">
                    {pricingLoading ? '...' : formatPrice(displayPrice)}
                  </div>
                  {isSaleActive && savingsAmount > 0 && (
                    <>
                      <div className="text-4xl text-gray-300 line-through font-semibold decoration-2 mb-2">{formatPrice(originalPrice)}</div>
                      <div className="text-xl text-green-400 font-semibold">Save {formatPrice(savingsAmount)} ({savingsPercentage}% OFF)</div>
                    </>
                  )}
                  {perCoursePrice > 0 && (
                    <div className="text-sm text-gray-400 mt-2">or just {formatPrice(perCoursePrice)} per course</div>
                  )}
                </div>
              </div>

              <div className="space-y-4 mb-8">
                <div className="flex items-center justify-center gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400" />
                  <span className="text-lg">15 hours of comprehensive training</span>
                </div>
                <div className="flex items-center justify-center gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400" />
                  <span className="text-lg">$3,000+ in resources & templates</span>
                </div>
                <div className="flex items-center justify-center gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400" />
                  <span className="text-lg">Lifetime access to both courses</span>
                </div>
                <div className="flex items-center justify-center gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400" />
                  <span className="text-lg">2 professional certificates</span>
                </div>
              </div>

              <button
                onClick={handleEnrollClick}
                className="w-full bg-gradient-to-r from-purple-600 via-blue-600 to-cyan-600 hover:from-purple-500 hover:via-blue-500 hover:to-cyan-500 py-6 rounded-xl font-bold text-2xl flex items-center justify-center space-x-3 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-purple-500/50 group mb-6"
              >
                <span>Get Complete Bundle - {pricingLoading ? '...' : formatPrice(displayPrice)}</span>
                <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
              </button>

              <div className="flex items-center justify-center gap-2 text-gray-400">
                <Shield className="w-5 h-5 text-green-400" />
                <span className="text-sm">Secure Payment • Instant Access</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 px-6 bg-black">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-6">Frequently Asked Questions</h2>
          </div>

          <div className="space-y-4">
            {[
              {
                q: 'Can I purchase the courses separately?',
                a: `Yes, but you'd pay ${formatPrice(combinedIndividualPrice)} total. The bundle ${savingsAmount > 0 ? `saves you ${formatPrice(savingsAmount)} and ` : ''}gives you instant access to both for just ${formatPrice(displayPrice)}.`
              },
              {
                q: 'How long do I have access?',
                a: 'Lifetime! You get unlimited access to both courses forever, including all future updates and new content.'
              },
              {
                q: 'What support is available?',
                a: 'You get access to our dedicated support team to help you get the most out of your courses. Contact us anytime with questions.'
              },
              {
                q: 'Do I need any prior experience?',
                a: 'No! Both courses start from the basics. Whether you\'re a complete beginner or have some AI experience, you\'ll benefit.'
              },
              {
                q: 'Can I take the courses at my own pace?',
                a: 'Absolutely! Learn at your own pace with lifetime access. Revisit any lesson anytime you need.'
              },
              {
                q: 'Is this really worth $3,000+ in value?',
                a: 'Yes! Just the prompt templates and tool integration guides alone are worth thousands. Plus you get 15 hours of expert training.'
              }
            ].map((faq, idx) => (
              <div key={idx} className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl border border-white/10 overflow-hidden">
                <button
                  onClick={() => handleFaqClick(idx, faq.q)}
                  className="w-full p-6 text-left flex items-center justify-between hover:bg-white/5 transition-colors"
                >
                  <h3 className="text-xl font-bold bg-gradient-to-r from-purple-400 via-blue-400 to-cyan-400 bg-clip-text text-transparent pr-4">{faq.q}</h3>
                  {expandedFaq === idx ? (
                    <ChevronUp className="w-5 h-5 text-purple-400 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  )}
                </button>
                {expandedFaq === idx && (
                  <div className="px-6 pb-6">
                    <p className="text-gray-300 leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
